import { useCallback } from "react";
import { yupResolver } from "@hookform/resolvers/yup";

import Grid from "@mui/material/Grid";

import { FormSelectField } from "@covertree/ui/forms/FormSelectField";
import { useFormWithNotification } from "@covertree/hooks";
import { FormConditionRadioGroup, FormTextField } from "@covertree/ui";

import { useGetClaimDetailsForLossType } from "@hooks/useGetClaimDetailsForLossType";
import { useAppDispatch } from "@redux/hooks";
import { LossType, SourceOfDamageWeather } from "@api/operations.generated";
import { StepPath } from "@routing/paths";
import { submitClaimDetails } from "@redux/slices";

import { InjuredPersonsSection } from "../shared/InjuredPersonsSection";
import { validation, WeatherOrPropertyDamageForm } from "./form";
import { ClaimsForm } from "../../ClaimsForm";

const sourceOfDamageOptions = [
  { label: "Lightning", value: SourceOfDamageWeather.Lightning },
  { label: "Hail", value: SourceOfDamageWeather.Hail },
  {
    label: "Weight from Snow/Ice",
    value: SourceOfDamageWeather.WeightFromSnowOrIce,
  },
  { label: "Wind", value: SourceOfDamageWeather.Wind },
  { label: "Flood", value: SourceOfDamageWeather.Flood },
  { label: "Other", value: SourceOfDamageWeather.Other },
];

export function WeatherOrPropertyDamage() {
  const dispatch = useAppDispatch();

  const defaultValues = useGetClaimDetailsForLossType();

  const form = useFormWithNotification({
    mode: "all",
    reValidateMode: "onSubmit",
    defaultValues: { ...defaultValues },
    resolver: yupResolver(validation),
  });

  const { control } = form;

  const submit = useCallback((value: WeatherOrPropertyDamageForm) => {
    dispatch(
      submitClaimDetails({
        value,
        type: LossType.WeatherOrPropertyDamage,
      })
    );
  }, []);

  return (
    <ClaimsForm
      form={form}
      onSubmit={submit}
      navigateToStep={StepPath.UPLOAD_CLAIM_DOCUMENTS}
      dataTestId="weather-or-property-damage">
      <Grid item xs={12}>
        <FormSelectField
          id="claim-type"
          dataTestId="claim-type"
          name="claimType"
          options={sourceOfDamageOptions}
          label="Source of Damage"
          control={control}
        />
      </Grid>
      <Grid item xs={12}>
        <FormTextField
          id="claim-description"
          dataTestId="claim-description"
          name="claimDescription"
          rows={4}
          multiline
          label="Please describe the damage to the home. How do you think it happened?"
          control={control}
        />
      </Grid>
      <Grid item xs={12}>
        <FormConditionRadioGroup
          id="is-damage-fixes"
          control={control}
          name="isDamageFixed"
          label="Has the damage been repaired?"
        />
      </Grid>
      <Grid item xs={12}>
        <InjuredPersonsSection />
      </Grid>
    </ClaimsForm>
  );
}
